const BRAND_WORDS = new Set(["cas", "casa", "casmedia", "casemedia", "media", "solutions", "solution"]);

export function isBrandQuery(value) {
  const tokens = String(value || "").toLowerCase().split(/[^a-z0-9]+/).filter((part) => part.length > 1);
  return tokens.length > 0 && tokens.every((part) => BRAND_WORDS.has(part));
}

function rankingNumbers(row) {
  return {
    clicks: Number(row?.clicks || 0),
    impressions: Number(row?.impressions || 0),
    position: Number(row?.position || 0),
  };
}

/** Only a search that was shown often, got no clicks, and is not the company name can be fixed. */
export function searchRowStatus(row) {
  const { clicks, impressions, position } = rankingNumbers(row);
  const brand = isBrandQuery(row?.query);
  if (brand && !(clicks > 0 && position > 0 && position <= 8)) {
    return {
      action: "wait",
      label: "Name typed wrong",
      detail: "Someone mistyped the company name. There are not enough searches to change a title.",
    };
  }
  if (brand || (position > 0 && position <= 8 && clicks > 0)) {
    return {
      action: "keep",
      label: "Already ranking",
      detail: "People already find the site with this search. Leave the title as it is.",
    };
  }
  if (!brand && impressions >= 10 && clicks === 0 && position > 10) {
    return {
      action: "fix",
      label: "Fix the title",
      detail: "Google showed this search many times and nobody clicked. Add these words to the page title.",
    };
  }
  return {
    action: "wait",
    label: "Too few searches",
    detail: "Google barely showed this search. There is nothing to change yet.",
  };
}

/** A page is worth a title edit only when it was shown often, got no clicks, and sits far down. */
export function pageRowStatus(row) {
  const { clicks, impressions, position } = rankingNumbers(row);
  if (impressions >= 20 && clicks === 0 && position > 15) {
    return {
      action: "fix",
      label: "Fix the title",
      detail: "Google showed this page often and nobody clicked. Match the title to the search, then Save and Publish.",
    };
  }
  if (clicks > 0 && position > 0 && position <= 10) {
    return {
      action: "keep",
      label: "Already getting clicks",
      detail: "This page already appears and people open it. Leave the title as it is.",
    };
  }
  return {
    action: "wait",
    label: "Too few searches",
    detail: "Google only showed this page a handful of times. Wait for more searches.",
  };
}

export function followUpView(task) {
  const title = task?.title || "Follow-up";
  const notes = task?.notes || "";
  const clicks = Number((notes.match(/clicks=([\d.]+)/) || [])[1] || 0);
  const impressions = Number((notes.match(/impressions=([\d.]+)/) || [])[1] || 0);
  const position = Number((notes.match(/pos=([\d.]+)/) || [])[1] || 0);
  const queryName = title.match(/^GSC query:\s*(.*)/i)?.[1];
  const pageName = title.match(/^GSC page:\s*(.*)/i)?.[1];
  if (queryName) {
    return {
      name: queryName,
      kind: "Search",
      path: "",
      status: searchRowStatus({ query: queryName, clicks, impressions, position }),
    };
  }
  if (pageName) {
    const href = pageName.startsWith("http") ? pageName : `https://www.casmediasolutions.com${pageName.startsWith("/") ? pageName : `/${pageName}`}`;
    let path = pageName;
    try { path = new URL(href).pathname || "/"; } catch { /* keep the stored path */ }
    return {
      name: pageDisplayName(href),
      kind: "Page",
      path,
      status: pageRowStatus({ clicks, impressions, position }),
    };
  }
  return {
    name: title,
    kind: "Note",
    path: "",
    status: { action: "wait", label: "No page linked", detail: "This note is not tied to a page you can edit." },
  };
}

export function pageDisplayName(url) {
  try {
    const path = new URL(url).pathname.replace(/\/$/, "") || "/";
    if (path === "/") return "Homepage";
    const last = path.split("/").filter(Boolean).pop() || path;
    return last.replace(/-/g, " ");
  } catch {
    return url || "Page";
  }
}

export function live(field, fallback = "") {
  if (!field || typeof field !== "object") return field || fallback;
  return field.published || field.configured || field.detected || fallback;
}

export function bandColor(score) {
  if (score == null) return "#64748B";
  if (score >= 85) return "#10B981";
  if (score >= 70) return "#3B82F6";
  if (score >= 50) return "#F59E0B";
  return "#EF4444";
}

export function TripleHint({ field }) {
  if (!field) return null;
  return (
    <div className="grid grid-cols-3 gap-2 text-[10px] text-white/45 mt-1">
      <div><span className="uppercase tracking-wider text-white/30">Detected</span><div className="text-white/70 truncate">{field.detected || "—"}</div></div>
      <div><span className="uppercase tracking-wider text-white/30">Draft</span><div className="text-cyan-200/80 truncate">{field.configured || "—"}</div></div>
      <div><span className="uppercase tracking-wider text-white/30">Published</span><div className="text-emerald-300/80 truncate">{field.published || "not published"}</div></div>
    </div>
  );
}

export function hasUnpublishedChanges(page) {
  const check = (field) => {
    if (!field || typeof field !== "object") return false;
    const c = field.configured || "";
    const p = field.published || "";
    return Boolean(c && c !== p);
  };
  const seo = page?.seo || {};
  const technical = page?.technical || {};
  const social = page?.social || {};
  const og = social.open_graph || {};
  const tw = social.twitter || {};
  const socialDirty = Boolean(
    (og.title || og.description || og.image || og.url) && page?.publish_state !== "published",
  ) || Boolean((tw.title || tw.image) && page?.publish_state !== "published");
  return (
    check(seo.title)
    || check(seo.description)
    || check(technical.canonical)
    || check(technical.robots)
    || check(page?.schema)
    || socialDirty
  );
}

export const PUBLISH_REBUILD_HINT =
  "API is live now. Rebuild & deploy the website for view-source / crawler first-paint HTML. Robots & sitemap update without rebuild.";

export function publishRebuildHint(measurement) {
  const hook = measurement?.deploy_hook;
  if (hook?.configured && hook?.enabled !== false) {
    return "API is live now. Cloudflare Pages rebuild is triggered automatically on publish (deploy hook). Robots, sitemap, llms.txt update without rebuild.";
  }
  return PUBLISH_REBUILD_HINT;
}

/**
 * Admin routes where page copy / H1 / sections are actually edited.
 */
export function adminContentEditLinks(page) {
  const path = (page?.path || "/").replace(/\/$/, "") || "/";
  const links = [];

  const push = (to, label, hint) => {
    if (!to || links.some((l) => l.to === to)) return;
    links.push({ to, label, hint });
  };

  if (path === "/contact") {
    push(
      "/site-settings?panel=section-headers&section=contact_page",
      "Edit Contact heading (H1)",
      "Brand & Pages → Sections → Contact Page — title becomes the H1",
    );
    push(
      "/site-settings?panel=contact",
      "Edit Contact details",
      "Phone, email, address on Brand & Pages → Contact",
    );
    push(
      "/site-settings?panel=cta",
      "Edit contact form / CTA",
      "Brand & Pages → CTA",
    );
  } else if (path === "/") {
    push("/home-builder", "Edit Home Builder", "Homepage sections and hero blocks");
    push("/site-settings?panel=hero", "Edit Hero copy", "Brand & Pages → Hero");
  } else if (path === "/about") {
    push("/site-settings?panel=about", "Edit About copy", "Brand & Pages → About");
  } else if (path === "/blog") {
    push(
      "/site-settings?panel=section-headers&section=blog",
      "Edit Blog page heading",
      "Brand & Pages → Sections → Blog",
    );
    push("/blog", "Manage blog posts", "Create and edit articles");
  } else if (path.startsWith("/blog/")) {
    const slug = path.replace("/blog/", "");
    push(`/blog?edit=${encodeURIComponent(slug)}`, "Edit this blog post", "Opens the article editor");
  } else if (path === "/services") {
    push("/services", "Manage services", "Services list and detail pages");
    push(
      "/site-settings?panel=services-header",
      "Edit Services heading",
      "Brand & Pages → Services",
    );
  } else if (path.startsWith("/services/")) {
    const slug = path.replace("/services/", "");
    push(`/services?edit=${encodeURIComponent(slug)}`, "Edit this service", "Opens the service editor");
  } else if (path === "/portfolio") {
    push("/portfolio", "Manage portfolio", "Portfolio items");
    push(
      "/site-settings?panel=section-headers&section=portfolio",
      "Edit Portfolio heading",
      "Brand & Pages → Sections → Portfolio",
    );
  } else if (path.startsWith("/portfolio/")) {
    const slug = path.replace("/portfolio/", "");
    push(`/portfolio?edit=${encodeURIComponent(slug)}`, "Edit this portfolio item", "Opens the portfolio editor");
  } else if (path === "/pricing") {
    push("/site-settings?panel=pricing", "Edit pricing", "Brand & Pages → Pricing");
    push(
      "/site-settings?panel=section-headers&section=pricing",
      "Edit pricing heading",
      "Brand & Pages → Sections → Pricing"
    );
  } else if (path === "/faq") {
    push("/faq", "Edit FAQ", "FAQ entries in admin");
  } else if (path === "/careers") {
    push("/site-settings?panel=careers", "Edit Careers copy", "Brand & Pages → Careers");
  } else if (path === "/team") {
    push("/team", "Edit team", "Team members in admin");
    push(
      "/site-settings?panel=section-headers&section=team",
      "Edit Team heading",
      "Brand & Pages → Sections → Team",
    );
  } else {
    push("/site-settings", "Brand & Pages studio", "Site-wide copy and section headings");
  }

  return links;
}
